import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { PageHeader } from "@/components/PageHeader";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Sheet, SheetContent, SheetTrigger, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import StatusPill from "@/components/StatusPill";
import { ArrowUp, Lightbulb, Plus } from "lucide-react";
import { fmtDate, titleCase } from "@/lib/format";
import { toast } from "sonner";

const STATUSES = ["submitted", "planned", "in_progress", "released", "rejected"];

export default function FeatureRequests() {
    const { user } = useAuth();
    const [items, setItems] = useState([]);
    const [openNew, setOpenNew] = useState(false);
    const [form, setForm] = useState({ title: "", description: "", category: "general" });

    const load = () => api.get("/feature-requests").then(({ data }) => setItems(data));
    useEffect(() => { load(); }, []);

    const vote = async (id) => {
        try { await api.post(`/feature-requests/${id}/vote`); load(); } catch { toast.error("Failed"); }
    };

    const updateStatus = async (id, status) => {
        try { await api.patch(`/feature-requests/${id}`, { status }); load(); } catch { toast.error("Unauthorized"); }
    };

    const submit = async () => {
        if (!form.title) return toast.error("Title required");
        try {
            await api.post("/feature-requests", form);
            toast.success("Submitted");
            setForm({ title: "", description: "", category: "general" });
            setOpenNew(false);
            load();
        } catch { toast.error("Failed"); }
    };

    return (
        <div data-testid="feature-requests-page">
            <PageHeader
                title="Feature Requests"
                subtitle="Office staff and (soon) HaulerOS customers can propose improvements. Vote on what matters most."
                actions={
                    <Sheet open={openNew} onOpenChange={setOpenNew}>
                        <SheetTrigger asChild>
                            <Button size="sm" data-testid="new-feature-btn"><Plus className="w-4 h-4 mr-1" />New request</Button>
                        </SheetTrigger>
                        <SheetContent className="w-full sm:max-w-lg">
                            <SheetHeader><SheetTitle className="font-heading text-2xl">New feature request</SheetTitle>
                                <SheetDescription className="sr-only">Suggest an improvement to the platform.</SheetDescription>
                            </SheetHeader>
                            <div className="space-y-3 mt-6">
                                <div className="space-y-1.5"><Label className="text-xs">Title</Label>
                                    <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="fr-title" /></div>
                                <div className="space-y-1.5"><Label className="text-xs">Description</Label>
                                    <Textarea rows={4} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="fr-description" /></div>
                                <div className="space-y-1.5"><Label className="text-xs">Category</Label>
                                    <Input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} /></div>
                                <Button onClick={submit} className="w-full" data-testid="fr-submit">Submit</Button>
                            </div>
                        </SheetContent>
                    </Sheet>
                }
            />
            <div className="px-8 py-6 space-y-3" data-testid="feature-list">
                {items.map((fr) => (
                    <Card key={fr.id} data-testid={`fr-card-${fr.id}`}>
                        <CardContent className="p-5 flex items-start gap-5">
                            <button onClick={() => vote(fr.id)}
                                    data-testid={`fr-vote-${fr.id}`}
                                    className={`flex flex-col items-center justify-center w-14 h-16 rounded-md border transition-all ${
                                        fr.voters?.includes(user?.id) ? "border-primary bg-primary/10 text-primary" : "border-border hover:border-primary"
                                    }`}>
                                <ArrowUp className="w-4 h-4 mb-0.5" />
                                <div className="font-bold tabular text-lg">{fr.votes}</div>
                            </button>
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-3 mb-2">
                                    <Lightbulb className="w-4 h-4 text-amber-500" />
                                    <h4 className="font-heading text-lg font-semibold">{fr.title}</h4>
                                    <StatusPill status={fr.status} />
                                </div>
                                <p className="text-sm text-muted-foreground mb-2">{fr.description}</p>
                                <div className="text-xs text-muted-foreground flex items-center gap-3">
                                    <span>{titleCase(fr.category)}</span> · <span>{fmtDate(fr.created_at)}</span>
                                </div>
                            </div>
                            {user?.role === "admin" && (
                                <Select value={fr.status} onValueChange={(v) => updateStatus(fr.id, v)}>
                                    <SelectTrigger className="w-36" data-testid={`fr-status-${fr.id}`}><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        {STATUSES.map((s) => <SelectItem key={s} value={s}>{titleCase(s)}</SelectItem>)}
                                    </SelectContent>
                                </Select>
                            )}
                        </CardContent>
                    </Card>
                ))}
                {items.length === 0 && <div className="text-center text-muted-foreground py-12">No feature requests yet.</div>}
            </div>
        </div>
    );
}
